import { ShoppingCart, Zap, Shield, ArrowRight, Lock, CheckCircle2, ChevronDown, ChevronUp, Mail } from "lucide-react";
import { useOrderUuid, useSetOrderUuid } from "@/stores/orderIdStore";
import { useNewOrder } from "@/queries/order/useNewOrder";
import { useUpdateNewOrder } from "@/queries/order/useUpdateNewOrder";
import { formatPrice } from "@/util/formatPrice";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { useCompleteStep } from "../hook";
import { useBuyPanelsStore } from "../store";
import { useAuthStore } from "@/stores/authStore";
import { useUser } from "@/queries/user/useUser";
import { findDefaultEmail } from "@/util/user";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { TrackableEventNameEnum } from "react-meta-pixel";
import { usePixelContext } from "@/contexts/MetaPixelContext";
import PaymentTermsDialog from "./PaymentTermsDialog";
import { isBuySolarPath } from "../route";
import { useLocation } from "react-router";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OrderSummaryCard() {
  const { pixel } = usePixelContext();
  const location = useLocation();
  const orderUuid = useOrderUuid();
  const { data: order } = useNewOrder({ uuid: orderUuid! });
  const setOrderUuid = useSetOrderUuid();
  const completeStep = useCompleteStep();
  const setStepIncomplete = useBuyPanelsStore(
    (state) => state.setStepIncomplete
  );
  const { data: user } = useUser();

  const [isEmailSectionOpen, setIsEmailSectionOpen] = useState(false);

  const { mutate: updateOrder, isPending } = useUpdateNewOrder({
    mutationConfig: {
      onSuccess: (data) => {
        if (pixel) {
          pixel.trackEvent(TrackableEventNameEnum.InitiateCheckout, {
            currency: "AUD",
            value: data.totalPrice,
            num_items: data.items?.reduce(
              (total, item) => total + (item.units ?? 0) / 100,
              0
            ),
          });
        }
        setOrderUuid(data.uuid!);
        completeStep();
      },
      onError: () => {
        toast.error("Failed to create order. Please try again.");
        setStepIncomplete("buypanels");
      },
    },
  });

  const handleProceed = () => {
    if (!order?.items?.length) {
      toast.error("Please select at least one panel to continue.");
      return;
    }
    const emailInputVisible = !findDefaultEmail(user as any)?.address;
    if (emailInputVisible) {
      const v = (emailInput || "").trim();
      if (v && !EMAIL_REGEX.test(v)) {
        setEmailError("Please enter a valid email address.");
        setStepIncomplete("buypanels");
        return;
      }
      setEmailError("");
    }

    const recurringEnabled = (billingOption ?? (effectivePaymentType === "recurring" ? "recurring" : "oneoff")) === "recurring";

    if (recurringEnabled) {
      if (!frequency) {
        toast.error("Please choose a recurring frequency.");
        setStepIncomplete("buypanels");
        return;
      }

      if (endCondition === "certain-date" && !endDate) {
        toast.error("Please choose a recurring end date.");
        setStepIncomplete("buypanels");
        return;
      }

      if (endCondition === "number-of-payments" && !paymentCount) {
        toast.error("Please enter a recurring payment count.");
        setStepIncomplete("buypanels");
        return;
      }
    }

    const existingUserEmail = findDefaultEmail(user as any)?.address;
    const buyStoreEmail = useBuyPanelsStore.getState().data.email;
    const orderOwnerEmail = order?.owner?.email?.address || (order?.owner?.emails && order.owner.emails[0]);

    const finalEmail = existingUserEmail || orderOwnerEmail || buyStoreEmail || emailInput.trim() || "";

    try {
      if (finalEmail) {
        useAuthStore.getState().setData({ email: finalEmail });
      }
    } catch (e) {
      console.error("Error persisting email:", e);
    }

    const updatePayload = {
      ...order,
      billingOption: recurringEnabled ? "recurring" : "oneoff",
      recurringFrequency: recurringEnabled ? frequency : undefined,
      recurringEndCondition: recurringEnabled ? endCondition : undefined,
      recurringEndDate: recurringEnabled ? endDate || undefined : undefined,
      recurringPaymentCount:
        recurringEnabled && paymentCount && Number.isFinite(Number(paymentCount))
          ? Number(paymentCount)
          : undefined,
    };

    updateOrder(updatePayload);
  };

  const buyStoreEmail = useBuyPanelsStore((s) => s.data.email) ?? "";
  const paymentType = useBuyPanelsStore((s) => s.paymentType);
  const billingOption = useBuyPanelsStore((s) => s.billingOption);
  const frequency = useBuyPanelsStore((s) => s.frequency);
  const endCondition = useBuyPanelsStore((s) => s.endCondition);
  const endDate = useBuyPanelsStore((s) => s.endDate);
  const paymentCount = useBuyPanelsStore((s) => s.paymentCount);
  const setBuyData = useBuyPanelsStore((s) => s.setData);
  const isVariant1 = isBuySolarPath(location.pathname);
  const effectivePaymentType = isVariant1 ? paymentType : "once";
  const [emailInput, setEmailInput] = useState<string>(buyStoreEmail || "");
  const [emailError, setEmailError] = useState<string>("");

  useEffect(() => {
    setEmailInput(buyStoreEmail || "");
  }, [buyStoreEmail]);

  if (!order) return null;

  const totalPanels = order.items?.reduce(
    (sum, item) => sum + (item.units ?? 0) / 100,
    0
  );
  // Calculate system capacity in kW from total watts
  const systemCapacity = (order.items?.reduce(
    (sum, item) => sum + (item.units ?? 0),
    0
  ) ?? 0) / 1000;
  const total = order?.totalPrice ?? 0;
  const deposit = order?.totalDeposit ?? 0;
  const depositPercentageFromPricing =
    order?.items && order.items.length > 0
      ? order.items[0].pricing?.depositPercentage ?? 10
      : 10;

  const fullPriceFromOrder =
    order?.fullPrice ??
    (order.items ?? []).reduce(
      (sum, item) =>
        sum + (item.itemFullPrice ?? (item.units ?? 0) * (item.unitPrice ?? item.product?.wattPriceCurrent ?? 0)),
      0
    );

  const originalDeposit = Number(((fullPriceFromOrder * depositPercentageFromPricing) / 100).toFixed(2));

  const depositPercentage = total > 0 ? (deposit / total) * 100 : 0;
  const discountPercent = fullPriceFromOrder > 0 ? ((fullPriceFromOrder - total) / fullPriceFromOrder) * 100 : 0;
  const recurringCharge = total;

  // Get panel breakdown by warranty
  const panelBreakdown = order.items?.map((item) => ({
    warranty: item.product?.name || "",
    quantity: (item.units ?? 0) / 100,
    price: item.itemTotal ?? (item.units ?? 0) * (item.unitPrice ?? 0),
  }));

  return (
    <div className="sticky top-6 space-y-4">
      {/* Main Summary Card */}
      <Card className="py-0 bg-[#181C24] border border-zinc-800 rounded-xl">
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="w-5 h-5 text-brand-orange-500" />
            <h2 className="text-xl font-bold text-white">Order Summary</h2>
          </div>

          {/* System Capacity */}
          <Card className="bg-[#0e1015] border border-zinc-800 py-0 rounded-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Zap className="w-4 h-4 text-brand-orange-500" />
                <span className="text-xs font-semibold text-gray-400">System Capacity</span>
              </div>
              <div className="text-2xl font-bold text-white mb-0.5">
                {systemCapacity.toFixed(1)} kW
              </div>
              <div className="text-xs text-gray-400">
                {totalPanels} SolarCloud Solar {totalPanels === 1 ? "Panel" : "Panels"}
              </div>
            </CardContent>
          </Card>

          {/* Panel Breakdown */}
          <Card className="bg-[#0e1015] border border-zinc-800 py-0 rounded-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-brand-orange-500" />
                <span className="text-xs font-semibold text-gray-400">Panel Breakdown</span>
              </div>
              <div className="space-y-2">
                {panelBreakdown && panelBreakdown.length > 0 ? (
                  panelBreakdown.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-xs">
                      <span className="text-gray-300">
                        {item.warranty} × {item.quantity}
                      </span>
                      <span className="font-semibold text-white">{formatPrice(item.price)}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-500">No panels selected yet</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Total Price Row */}
          <div className="flex justify-between items-center pt-2">
            <span className="text-base font-medium text-white">Total</span>
            <div className="text-right">
              <span className="text-xl font-bold text-white">{formatPrice(total)}</span>
              {(order?.fullPrice ?? 0) > total && (
                <div className="text-xs text-gray-500 line-through">
                  {formatPrice(order?.fullPrice ?? 0)}
                </div>
              )}
            </div>
          </div>

          {/* Deposit Highlight Card */}
          <Card className="bg-[#1c1917] border-2 border-brand-orange-500 rounded-xl py-0 shadow-lg shadow-black/40">
            <CardContent className="p-4 sm:p-5">
              {effectivePaymentType === "once" ? (
                <>
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5 mb-2">
                    <span>Only pay {depositPercentage.toFixed(0)}% today</span>
                    <PaymentTermsDialog />
                  </div>
                  {discountPercent > 0 ? (
                    <div className="mb-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm text-gray-400 line-through decoration-gray-400">
                          {formatPrice(originalDeposit)}
                        </span>
                        <span className="text-xs text-red-500 font-semibold">
                          -{Math.round(discountPercent)}%
                        </span>
                      </div>
                      <div className="text-3xl sm:text-4xl font-extrabold text-brand-orange-500">
                        {formatPrice(deposit)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-3xl sm:text-4xl font-extrabold text-brand-orange-500 mb-2">
                      {formatPrice(deposit)}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Remaining amount due only when your solar is ready.
                  </p>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5 mb-2">
                    <span>Full payment each cycle</span>
                    <PaymentTermsDialog />
                  </div>
                  <div className="flex items-baseline gap-1.5 mb-2">
                    <span className="text-3xl sm:text-4xl font-extrabold text-brand-orange-500">
                      {formatPrice(recurringCharge)}
                    </span>
                    <span className="text-sm text-gray-400 font-medium">
                      /{frequency?.toLowerCase() || "..."}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed mb-3">
                    Charged immediately on order placement, then on each selected cycle.
                  </p>
                  <div className="border-t border-zinc-800 pt-3 space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Frequency:</span>
                      <span className="text-white font-medium capitalize">{frequency || "..."}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Quantity:</span>
                      <span className="text-white font-medium">
                        {totalPanels} {totalPanels === 1 ? "panel" : "panels"}
                      </span>
                    </div>
                    {endCondition === "number-of-payments" && paymentCount && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Duration:</span>
                        <span className="text-white font-medium">{paymentCount} payments</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* CTA Button */}
          <div>
            <Button
              onClick={handleProceed}
              disabled={!order?.items?.length || isPending}
              size="lg"
              className="w-full bg-brand-orange-500 hover:bg-brand-orange-600 text-black font-bold text-base sm:text-lg py-3 rounded-lg shadow-md transition-all flex items-center justify-center gap-2"
            >
              <span>
                {isBuySolarPath(location.pathname)
                  ? "Next"
                  : effectivePaymentType === "once"
                  ? `Pay ${depositPercentage.toFixed(0)}% Deposit`
                  : "Proceed to Checkout"}
              </span>
              <ArrowRight className="w-5 h-5" />
            </Button>
            <div className="flex items-center justify-center gap-1.5 mt-2.5 text-xs text-gray-400">
              <Lock className="w-3.5 h-3.5 text-brand-orange-500 shrink-0" />
              <span>Secure checkout. All payments encrypted.</span>
            </div>
          </div>

          {/* Benefits Checkmarks List */}
          <div className="space-y-2.5 pt-2 border-t border-zinc-800 text-xs sm:text-sm text-gray-300">
            <div className="flex items-center gap-2.5">
              <div className="w-4 h-4 rounded-full bg-brand-orange-500 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-[#0e1015] fill-brand-orange-500" />
              </div>
              <span>The GST is part of the total and inclusive in the price.</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-4 h-4 rounded-full bg-brand-orange-500 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-[#0e1015] fill-brand-orange-500" />
              </div>
              <span>100% deposit refund policy</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-4 h-4 rounded-full bg-brand-orange-500 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-[#0e1015] fill-brand-orange-500" />
              </div>
              <span>Comprehensive warranty</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-4 h-4 rounded-full bg-brand-orange-500 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-[#0e1015] fill-brand-orange-500" />
              </div>
              <span>Australian owned &amp; operated</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Send me my order details Card (Collapsed by default) */}
      {!findDefaultEmail(user as any)?.address && (
        <Card className="bg-[#181C24] border border-zinc-800 rounded-xl py-0">
          <CardContent className="p-4 sm:p-5">
            <button
              onClick={() => setIsEmailSectionOpen(!isEmailSectionOpen)}
              className="w-full flex items-center justify-between text-left cursor-pointer group"
              aria-expanded={isEmailSectionOpen}
              aria-controls="order-details-body"
            >
              <span className="font-semibold text-sm sm:text-base text-white group-hover:text-brand-orange-400 transition-colors">
                Send me my order details (optional)
              </span>
              {isEmailSectionOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-white" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-white" />
              )}
            </button>

            {isEmailSectionOpen && (
              <div id="order-details-body" className="mt-3 space-y-3 pt-3 border-t border-zinc-800">
                <p className="text-xs text-gray-400">
                  Enter your email and we'll send your order confirmation and updates. No spam.
                </p>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setEmailInput(v);
                      setEmailError("");
                      try {
                        const normalizedEmail = v.trim() || undefined;
                        setBuyData({ email: normalizedEmail });
                        useAuthStore.getState().setData({ email: normalizedEmail });
                      } catch (e) {}
                    }}
                    placeholder="you@example.com"
                    className={`w-full bg-[#0e1015] border rounded-lg pl-9 pr-3 py-2 text-white outline-none focus:border-brand-orange-500 text-xs sm:text-sm ${
                      emailError ? "border-red-500" : "border-zinc-700"
                    }`}
                  />
                </div>
                {emailError && (
                  <div className="text-red-400 text-xs mt-1">{emailError}</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
